import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import Logo from "./Logo";
import { destinations } from "@/data/destinations";
import { services } from "@/data/services";
import { site, telHref } from "@/data/content";
import { useT } from "@/i18n";

const Col = ({ title, links }) => (
  <div>
    <p className="eyebrow-light mb-6">{title}</p>
    <ul className="space-y-3">
      {links.map(([to, label]) => (
        <li key={to}><Link to={to} className="link-lux text-[14px] text-ivory/75 hover:text-ivory">{label}</Link></li>
      ))}
    </ul>
  </div>
);

export default function Footer() {
  const { t } = useT();
  return (
    <footer className="relative overflow-hidden bg-navy-950 text-ivory" data-testid="site-footer">
      <div className="container-lux pb-10 pt-24">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
          <div>
            <Logo size="lg" testid="footer-logo" />
            <p className="mt-8 max-w-sm text-[14px] leading-relaxed text-ivory/75">A boutique advisory for individuals, families and investors who expect discretion, precision and an exceptional standard of care.</p>
            <ul className="mt-8 space-y-5 text-[14px] text-ivory/80">
              {site.offices.map((o) => (
                <li key={o.label} className="flex gap-3" data-testid={`footer-office-${o.city.toLowerCase()}`}>
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span><span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{o.label}</span>{o.lines.slice(0, 2).join(", ")}<a href={telHref(o.phone)} className="mt-1 flex items-center gap-2 hover:text-gold"><Phone className="h-3.5 w-3.5 text-gold" />{o.phone}</a><span className="mt-1 block text-[13px] text-ivory/70">{o.hoursText} ({o.tzLabel})</span></span>
                </li>
              ))}
              <li className="flex gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" /><a href={`mailto:${site.email}`} className="hover:text-gold">{site.email}</a></li>
            </ul>
          </div>
          <Col title="Destinations" links={destinations.map((d) => [`/destinations/${d.slug}`, d.name])} />
          <Col title="Services" links={services.map((s) => [`/services/${s.slug}`, s.name])} />
          <Col title="The Firm" links={[["/about", "About Us"], ["/insights", "Insights"], ["/faq", "FAQ"], ["/eligibility", "Eligibility Assessment"], ["/contact", "Book a Consultation"]]} />
        </div>
        <p aria-hidden="true" className="pointer-events-none mt-20 select-none whitespace-nowrap text-center font-serif text-[18vw] font-light leading-none text-outline lg:text-[15vw]">Immistop</p>
        <div className="hairline mt-6" />
        <p className="mt-8 max-w-4xl text-[12px] leading-relaxed text-ivory/70" data-testid="footer-disclaimer">{t("footer.disclaimer")}</p>
        <div className="mt-8 flex flex-col gap-4 text-[12px] text-ivory/70 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {site.legalName}. All rights reserved.</p>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold" data-testid="footer-terms-link">Terms</Link>
            <Link to="/disclaimer" className="hover:text-gold" data-testid="footer-disclaimer-link">Disclaimer</Link>
            <Link to="/admin" className="hover:text-gold" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
